import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, subMonths } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { PlayEvent, PaginatedResponse, PlayEventSeries } from '@court-sync/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import { PlayEventCreateDrawer } from '@/components/play/PlayEventCreateDrawer';
import {
  PlayEventSeriesSection,
  type PlayEventSeriesSectionHandle,
} from '@/components/play/PlayEventSeriesSection';
import { PlayFeatureGate } from '@/components/play/PlayFeatureGate';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import {
  Badge,
  Button,
  Checkbox,
  Input,
  PaginatedTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type PaginatedTableColumn,
  type PaginationState,
} from '@/components/ui';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { playEventService } from '@/services/playEvent';
import {
  PLAY_EVENT_FORMAT_LABELS,
  PLAY_EVENT_SIDE_LABELS,
  PLAY_EVENT_STATUS_BADGE_VARIANT,
  PLAY_EVENT_STATUS_LABELS,
} from '@/lib/playEventLabels';
import { CalendarDays, ChevronRight, History, Plus, Search, Trophy } from 'lucide-react';

const PAGE_SIZE = 20;
const HISTORY_LOOKBACK_MONTHS = 12;

type PlayEventsTab = 'events' | 'series' | 'history';

const PLAY_EVENTS_TABS: { id: PlayEventsTab; label: string; mobileLabel: string }[] = [
  { id: 'events', label: 'Events', mobileLabel: 'Events' },
  { id: 'series', label: 'Series', mobileLabel: 'Series' },
  { id: 'history', label: 'History', mobileLabel: 'History' },
];

function parsePlayEventsTab(value: string | null): PlayEventsTab {
  if (value === 'series') return 'series';
  if (value === 'history') return 'history';
  return 'events';
}

function isPaginatedResponse(
  res: { success: boolean; data?: PlayEvent[]; pagination?: PaginationState }
): res is PaginatedResponse<PlayEvent> {
  return res.success && res.pagination != null;
}

function formatEventDate(event: PlayEvent): string {
  const start = event.startsAt instanceof Date ? event.startsAt : new Date(event.startsAt);
  return format(start, 'EEE d MMM yyyy');
}

function formatEventTimeRange(event: PlayEvent): string {
  const start = event.startsAt instanceof Date ? event.startsAt : new Date(event.startsAt);
  const end = event.endsAt instanceof Date ? event.endsAt : new Date(event.endsAt);
  return `${format(start, 'HH:mm')} – ${format(end, 'HH:mm')}`;
}

export default function PlayEventsPage() {
  const navigate = useNavigate();
  const seriesSectionRef = useRef<PlayEventSeriesSectionHandle>(null);
  const { currentClub } = useCurrentClub();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parsePlayEventsTab(searchParams.get('tab'));
  const seriesDrillId =
    activeTab === 'series' ? searchParams.get('seriesId') ?? undefined : undefined;
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [rows, setRows] = useState<PlayEvent[]>([]);
  const [pagination, setPagination] = useState<PaginationState | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PlayEvent['status'] | 'all'>('all');
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [drilledSeries, setDrilledSeries] = useState<PlayEventSeries | null>(null);

  const handleTabChange = (tab: PlayEventsTab) => {
    const next = new URLSearchParams(searchParams);
    next.delete('seriesId');
    if (tab === 'events') {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  const loadPage = useCallback(
    async (nextPage: number) => {
      if (!currentClub || activeTab === 'series') return;
      setLoading(true);
      try {
        const now = new Date();
        const listFilters =
          activeTab === 'history'
            ? {
                from: subMonths(now, HISTORY_LOOKBACK_MONTHS).toISOString(),
                to: now.toISOString(),
                startsAtOrder: 'desc' as const,
              }
            : {
                ...(showAllUpcoming ? { allUpcoming: true } : {}),
              };

        const res = await playEventService.listEvents(currentClub.organizationId, {
          page: nextPage,
          limit: PAGE_SIZE,
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
          ...(searchQuery ? { search: searchQuery } : {}),
          ...listFilters,
        });
        if (isPaginatedResponse(res)) {
          setRows(res.data ?? []);
          setPagination(res.pagination);
        } else {
          setRows([]);
          setPagination(null);
        }
      } finally {
        setLoading(false);
      }
    },
    [activeTab, currentClub, searchQuery, showAllUpcoming, statusFilter]
  );

  useEffect(() => {
    void loadPage(page);
  }, [loadPage, page]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      setSearchQuery((prev) => (prev === trimmed ? prev : trimmed));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, statusFilter, searchQuery, showAllUpcoming]);

  useEffect(() => {
    if (!currentClub || !seriesDrillId) {
      setDrilledSeries(null);
      return undefined;
    }
    let cancelled = false;
    void playEventService.getSeries(currentClub.organizationId, seriesDrillId).then((res) => {
      if (cancelled) return;
      setDrilledSeries(res.success && res.data ? res.data : null);
    });
    return () => {
      cancelled = true;
    };
  }, [currentClub, seriesDrillId]);

  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setCreateDrawerOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const openEvent = useCallback(
    (event: PlayEvent) => {
      navigate(`/admin/play-events/${event.id}`, {
        state: {
          returnTo:
            activeTab === 'history'
              ? '/admin/play-events?tab=history'
              : activeTab === 'series'
                ? '/admin/play-events?tab=series'
                : '/admin/play-events',
        },
      });
    },
    [activeTab, navigate]
  );

  const columns: PaginatedTableColumn<PlayEvent>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Event',
        render: (event) => (
          <div className="min-w-0">
            <p className="font-medium text-gray-900 dark:text-white">{event.name}</p>
            <p className="mt-0.5 text-xs text-muted-foreground md:hidden">
              {formatEventDate(event)} · {formatEventTimeRange(event)}
            </p>
          </div>
        ),
      },
      {
        key: 'when',
        header: 'When',
        className: 'hidden md:table-cell whitespace-nowrap',
        render: (event) => (
          <div>
            <p className="text-sm text-gray-900 dark:text-white">{formatEventDate(event)}</p>
            <p className="text-xs text-muted-foreground">{formatEventTimeRange(event)}</p>
          </div>
        ),
      },
      {
        key: 'format',
        header: 'Format',
        render: (event) => (
          <span className="text-sm text-muted-foreground">
            {PLAY_EVENT_FORMAT_LABELS[event.format]} · {PLAY_EVENT_SIDE_LABELS[event.side]}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (event) => (
          <Badge variant={PLAY_EVENT_STATUS_BADGE_VARIANT[event.status]}>
            {PLAY_EVENT_STATUS_LABELS[event.status]}
          </Badge>
        ),
      },
      {
        key: 'actions',
        header: '',
        className: 'w-10 text-right',
        render: () => (
          <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" aria-hidden />
        ),
      },
    ],
    []
  );

  const totalCount = pagination?.total ?? rows.length;
  const isUpcomingEmpty =
    activeTab === 'events' &&
    !loading &&
    totalCount === 0 &&
    !searchQuery &&
    statusFilter === 'all';

  const isHistoryEmpty =
    activeTab === 'history' &&
    !loading &&
    totalCount === 0 &&
    !searchQuery &&
    statusFilter === 'all';

  const pageSubtitle =
    activeTab === 'history'
      ? 'Past standalone events from the last 12 months. Series sessions are listed under Series or on the calendar.'
      : activeTab === 'series'
        ? 'Weekly templates for social play. Generated sessions appear on the calendar.'
        : 'One-off events and tournaments. Recurring sessions appear on the calendar; manage weekly templates on the Series tab.';

  const headerActions =
    activeTab === 'series' && !seriesDrillId ? (
      <Button type="button" onClick={() => seriesSectionRef.current?.openCreate()}>
        <Plus className="mr-2 h-4 w-4" aria-hidden />
        Create series
      </Button>
    ) : (
      <Button type="button" onClick={() => setCreateDrawerOpen(true)}>
        <Plus className="mr-2 h-4 w-4" aria-hidden />
        New event
      </Button>
    );

  return (
    <PlayFeatureGate>
      <div className="contents">
        <PageHeader
          title="Play events"
          subtitle={pageSubtitle}
          breadcrumbs={[
            { label: 'Play events', href: '/admin/play-events' },
            ...(activeTab === 'series'
              ? [
                  {
                    label: 'Series',
                    href: drilledSeries ? '/admin/play-events?tab=series' : undefined,
                  },
                ]
              : []),
            ...(activeTab === 'history' ? [{ label: 'History' }] : []),
            ...(drilledSeries ? [{ label: drilledSeries.name }] : []),
          ]}
          actions={headerActions}
        />

        <DashboardTabNav<PlayEventsTab>
          tabs={PLAY_EVENTS_TABS}
          activeTab={activeTab}
          onChange={handleTabChange}
          ariaLabel="Play sections"
        />

        {activeTab === 'series' ? (
          <PlayEventSeriesSection ref={seriesSectionRef} />
        ) : isUpcomingEmpty ? (
          <DashboardEmptyStateCard>
            <DashboardEmptyStateIcon icon={Trophy} />
            <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
              No play events yet
            </h3>
            <p className="mx-auto mb-6 max-w-md text-gray-500 dark:text-gray-400">
              Create a one-off social, round robin, or tournament. For weekly play, use the Series
              tab — sessions still show on the calendar.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button type="button" onClick={() => setCreateDrawerOpen(true)}>
                <Plus className="mr-2 h-4 w-4" aria-hidden />
                New event
              </Button>
              <Button type="button" variant="outline" onClick={() => handleTabChange('series')}>
                <CalendarDays className="mr-2 h-4 w-4" aria-hidden />
                Manage series
              </Button>
            </div>
          </DashboardEmptyStateCard>
        ) : isHistoryEmpty ? (
          <DashboardEmptyStateCard>
            <DashboardEmptyStateIcon icon={History} />
            <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
              No event history yet
            </h3>
            <p className="mx-auto mb-6 max-w-md text-gray-500 dark:text-gray-400">
              Completed and cancelled standalone events from the last {HISTORY_LOOKBACK_MONTHS}{' '}
              months will appear here. Upcoming events stay on the Events tab.
            </p>
            <Button type="button" variant="outline" onClick={() => handleTabChange('events')}>
              View upcoming events
            </Button>
          </DashboardEmptyStateCard>
        ) : (
          <DashboardDataCard
            toolbar={
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  {loading ? 'Loading events…' : `${totalCount} event${totalCount === 1 ? '' : 's'}`}
                  {activeTab === 'history'
                    ? ` · past ${HISTORY_LOOKBACK_MONTHS} months`
                    : !showAllUpcoming
                      ? ' · next 30 days'
                      : ''}
                </p>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  {activeTab === 'events' ? (
                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Checkbox
                        checked={showAllUpcoming}
                        onCheckedChange={(v) => setShowAllUpcoming(Boolean(v))}
                        aria-label="Show all upcoming events"
                      />
                      Show all upcoming
                    </label>
                  ) : null}
                  <div className="relative w-full sm:w-64">
                    <Search
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                      aria-hidden
                    />
                    <Input
                      type="search"
                      placeholder="Search events…"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="pl-9"
                      aria-label="Search play events"
                    />
                  </div>
                  <Select
                    value={statusFilter}
                    onValueChange={(v) => setStatusFilter(v as PlayEvent['status'] | 'all')}
                  >
                    <SelectTrigger className="w-full sm:w-[180px]" aria-label="Filter by status">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      {(Object.keys(PLAY_EVENT_STATUS_LABELS) as PlayEvent['status'][]).map((status) => (
                        <SelectItem key={status} value={status}>
                          {PLAY_EVENT_STATUS_LABELS[status]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            }
          >
            <PaginatedTable
              columns={columns}
              data={rows}
              loading={loading}
              pagination={pagination}
              onPageChange={setPage}
              onRowClick={openEvent}
              emptyMessage={
                searchQuery || statusFilter !== 'all'
                  ? 'No events match your filters.'
                  : activeTab === 'history'
                    ? `No past events in the last ${HISTORY_LOOKBACK_MONTHS} months.`
                    : 'No events found.'
              }
              noWrapperPadding
              mobileTitleColumnKey=""
            />
          </DashboardDataCard>
        )}

        {currentClub && (activeTab === 'events' || activeTab === 'history') ? (
          <PlayEventCreateDrawer
            organizationId={currentClub.organizationId}
            clubId={currentClub.id}
            timezone={currentClub.timezone}
            isOpen={createDrawerOpen}
            onClose={() => setCreateDrawerOpen(false)}
            onCreated={() => void loadPage(1)}
          />
        ) : null}
      </div>
    </PlayFeatureGate>
  );
}
